import React, { useState, useEffect } from 'react';
import { get, put } from '../services/api';

const FIXED_POSITIONS = [
  '船長','一等航海士','二等航海士','三等航海士',
  '機関長','一等機関士','二等機関士','三等機関士',
  '甲板長','甲板員','機関員','コック','その他',
];

interface PermKey { key: string; label: string; }
interface PermData {
  permissions: Record<string, Record<string, boolean>>;
  permission_keys: PermKey[];
  fixed_positions: string[];
}

const PositionPermissionsPage: React.FC = () => {
  const [data, setData]             = useState<PermData | null>(null);
  const [masterPositions, setMasterPositions] = useState<string[]>([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState<string | null>(null);
  const [toast, setToast]           = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [permRes, masterRes] = await Promise.all([
        get<{ data: PermData }>('/position-permissions'),
        get<{ data: { name: string; is_active: number }[] }>('/position-masters'),
      ]);
      setData(permRes.data);
      setMasterPositions(
        (masterRes?.data ?? [])
          .filter(m => m.is_active)
          .map(m => m.name)
      );
    } catch (err) { console.error('[PositionPermissionsPage]', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (position: string, permKey: string, current: boolean) => {
    const key = `${position}:${permKey}`;
    setSaving(key);
    try {
      await put('/position-permissions', { position, permission_key: permKey, is_allowed: !current });
      setData(prev => {
        if (!prev) return prev;
        const perms = { ...prev.permissions };
        if (!perms[position]) perms[position] = {};
        perms[position] = { ...perms[position], [permKey]: !current };
        return { ...prev, permissions: perms };
      });
      setToast('保存しました');
      setTimeout(() => setToast(''), 1500);
    } catch { setToast('保存に失敗しました'); setTimeout(() => setToast(''), 2000); }
    finally { setSaving(null); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;
  if (!data) return null;

  // 表示する役職: 固定選択肢 + 役職マスタで追加した役職 + 配置で実際に使われているその他の役職
  const masterExtra = masterPositions.filter(p => !FIXED_POSITIONS.includes(p));
  const usedPositions = Object.keys(data.permissions).filter(
    p => !FIXED_POSITIONS.includes(p) && !masterExtra.includes(p)
  );
  const allPositions = [...FIXED_POSITIONS, ...masterExtra, ...usedPositions];

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">役職権限設定</h1>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#0f172a', color: '#fff', padding: '10px 24px',
          borderRadius: 999, fontSize: 14, fontWeight: 700, zIndex: 100,
        }}>{toast}</div>
      )}

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1e40af' }}>
        各役職に許可する権限をON/OFFで設定します。乗組員配置で役職が設定されている船員に適用されます。<br />
        <span style={{ opacity: 0.9 }}>
          <strong>🖥 の付いた権限</strong>は「管理サイトの閲覧」設定です。<strong>「🖥 管理サイトを開く」をONにすると、その役職の船員は（陸上管理者でなくても）管理サイトに<u>制限付きで</u>ログイン</strong>でき、ONにした機能だけを閲覧できます（例：船長に勤怠・ヒヤリハットだけ許可）。
        </span><br />
        <span style={{ opacity: 0.8 }}>役職を追加・削除するには「マスタ管理 → 役職マスタ」から行ってください。</span>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
              <th style={{ padding: '12px 18px', textAlign: 'left', color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 140 }}>役職</th>
              {data.permission_keys.map(pk => (
                <th key={pk.key} style={{ padding: '12px 18px', textAlign: 'center', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 }}>
                  {pk.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allPositions.map((pos, i) => (
              <tr key={pos} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                <td style={{ padding: '12px 18px', fontWeight: 600, color: '#111827' }}>
                  <span style={{ background: '#EDE9FE', color: '#7C3AED', borderRadius: 20, padding: '3px 10px', fontSize: 12 }}>{pos}</span>
                </td>
                {data.permission_keys.map(pk => {
                  const allowed = data.permissions[pos]?.[pk.key] ?? false;
                  const key = `${pos}:${pk.key}`;
                  const isSaving = saving === key;
                  return (
                    <td key={pk.key} style={{ padding: '12px 18px', textAlign: 'center' }}>
                      <button
                        onClick={() => toggle(pos, pk.key, allowed)}
                        disabled={isSaving}
                        style={{
                          width: 44, height: 24, borderRadius: 12,
                          background: allowed ? '#0066CC' : '#D1D5DB',
                          border: 'none', cursor: 'pointer',
                          position: 'relative', transition: 'background 0.2s',
                          opacity: isSaving ? 0.6 : 1,
                        }}
                      >
                        <span style={{
                          position: 'absolute', top: 3,
                          left: allowed ? 23 : 3,
                          width: 18, height: 18, borderRadius: '50%',
                          background: '#fff', transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {masterPositions.length === 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginTop: 16, fontSize: 13, color: '#92400e' }}>
          ⚠ 役職マスタに役職が登録されていません。「マスタ管理 → 役職マスタ」から役職を追加すると、ここに表示されます。
        </div>
      )}
    </div>
  );
};

export default PositionPermissionsPage;
